import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

export const waveKeyframe = keyframes`
  0% { transform: scaleY(0.15); }
  50% { transform: scaleY(1); }
  100% { transform: scaleY(0.15); }
`;

export const RecordingWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 0 10px;
	height: 48px;
	grid-column: 1 / -1;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		height: 40px;
		padding: 0 6px;
	}
`;

export const WaveContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 3px;
	height: 30px;
	flex-grow: 1;
	padding: 0 20px;
	overflow: hidden;
`;

export const WaveBar = styled.div`
	width: 4px;
	background-color: white;
	border-radius: 4px;
	height: 100%;
	transform: scaleY(0.15);
	animation: ${waveKeyframe} ease-in-out infinite;
	animation-duration: ${({ duration }) => duration}s;
	animation-delay: ${({ delay }) => delay}s;
	opacity: ${({ isEdge }) => (isEdge ? 0.6 : 1)};
`;

export const TrashButton = styled.button`
	background: none;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #ff4d4d;
	padding: 8px;
	border-radius: 50%;
	transition: all 0.2s ease;

	&:hover {
		background-color: rgba(255, 77, 77, 0.1);
		transform: scale(1.1);
	}

	${({ disabled }) =>
		disabled &&
		css`
			opacity: 0;
			pointer-events: none;
		`}
`;

const voiceComposerTheme = {
	user: css`
		border-color: rgba(56, 189, 248, 0.34);
		background:
			radial-gradient(circle at 12% 50%, rgba(56, 189, 248, 0.16), transparent 52%),
			rgba(12, 34, 52, 0.48);
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 0 18px rgba(56, 189, 248, 0.14);
	`,
	character: css`
		border-color: rgba(255, 131, 233, 0.34);
		background:
			radial-gradient(circle at 12% 50%, rgba(255, 131, 233, 0.16), transparent 52%),
			rgba(44, 20, 50, 0.48);
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 0 18px rgba(221, 55, 255, 0.14);
	`,
	silent: css`
		border-color: rgba(255, 255, 255, 0.12);
		background: rgba(18, 18, 28, 0.42);
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
	`,
};

export const VoiceCallComposer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 0 14px;
	height: 48px;
	grid-column: 1 / -1;
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 999px;
	transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;

	${({ $waveMode }) => voiceComposerTheme[$waveMode] || voiceComposerTheme.silent}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		height: 40px;
		padding: 0 12px;
		border: 0;
		box-shadow: none;
	}
`;

const voiceStatusColor = {
	user: 'rgba(186, 230, 253, 0.95)',
	character: 'rgba(255, 214, 247, 0.95)',
	silent: 'rgba(210, 210, 220, 0.78)',
};

export const VoiceCallStatus = styled.span`
	font-size: 0.92rem;
	font-weight: 650;
	transition: color 180ms ease;
	color: ${({ $waveMode }) => voiceStatusColor[$waveMode] || voiceStatusColor.silent};

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 0.8125rem;
	}
`;

export const VoiceCallWave = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 3px;
	height: 22px;
`;

const voiceWaveBarTheme = {
	user: css`
		background: linear-gradient(#38bdf8, #22d3ee);
		box-shadow: 0 0 8px rgba(56, 189, 248, 0.5);
	`,
	character: css`
		background: linear-gradient(#ff83e9, #b855ff);
		box-shadow: 0 0 8px rgba(255, 80, 230, 0.55);
	`,
	silent: css`
		background: rgba(255, 255, 255, 0.22);
		box-shadow: none;
	`,
};

export const VoiceCallWaveBar = styled.span`
	width: 3px;
	height: ${({ $active }) => ($active ? '100%' : '24%')};
	border-radius: 999px;
	transform: scaleY(0.24);
	animation: ${waveKeyframe} ease-in-out infinite;
	animation-duration: ${({ duration }) => duration}s;
	animation-delay: ${({ delay }) => delay}s;
	animation-play-state: ${({ $active }) => ($active ? 'running' : 'paused')};
	opacity: ${({ $active }) => ($active ? 1 : 0.35)};
	transition: opacity 180ms ease;

	${({ $waveMode }) => voiceWaveBarTheme[$waveMode] || voiceWaveBarTheme.silent}
`;
